"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SliderHandleContainer = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var BrowserDetection_1 = require("../../utils/BrowserDetection");
var Declarative_1 = require("../../utils/Declarative");
var GlobalPointerEventCapturer_1 = require("../../utils/GlobalPointerEventCapturer");
var UserInteraction_1 = require("../../utils/UserInteraction");
var SliderBaseWidget_1 = require("./SliderBaseWidget");
var SliderHandleContainerCoordinateLogic = tslib_1.__importStar(require("./SliderHandleContainerCoordinateLogic"));
var SliderHandleContainerDragHandleLogic = tslib_1.__importStar(require("./SliderHandleContainerDragHandleLogic"));
var SliderHandleWidget_1 = require("./SliderHandleWidget");
var SliderStyles_1 = require("./SliderStyles");
var SliderValueLogic_1 = require("./SliderValueLogic");
/**
 * This component is responsible for displaying the handles and making them interactive.  I.e. dragging.
 *
 * It does not deal with the min and maxes or the actual value of the slider.  That is all handled in the
 * SliderWidget itself.
 *
 * Note that to deal with mouse/touch events outside the bounds of the element and window, we have to attach event
 * handlers to the window itself.
 */
var SliderHandleContainer = /** @class */ (function (_super) {
    tslib_1.__extends(SliderHandleContainer, _super);
    function SliderHandleContainer(props) {
        var _this = _super.call(this, props) || this;
        _this.globalPointerEventCapturer = undefined; // this gets set on mount
        // We only have to get this ref because we have to do setCapture and releaseCapture on IE.  Without that IE won't
        // send us move and end events for a drag once the pointer or finger leaves the bounds of the element (i.e. goes
        // off the page).  We let the GlobalPointerEventCapturer deal with the actual setCapture and releaseCapture.
        _this.containerElement = null;
        // We need this ref to get bounding client rects and to focus() it.
        _this.leftHandleWidgetRef = null;
        // We need this ref to get bounding client rects and to focus() it.
        _this.rightHandleWidgetRef = null;
        // We need this ref to focus the range slider.
        _this.rangeHandleElement = null;
        _this.getHandleCount = function () {
            return typeof _this.props.handles.rightHandlePosition === 'undefined' ? 1 : 2;
        };
        _this.isSingleHandleBeingDragged = function (handleType) {
            return handleType === SliderBaseWidget_1.HandleType.Left || handleType === SliderBaseWidget_1.HandleType.Right;
        };
        _this.determineSingleHandleDragOffsetIfNeeded = function (handleType, isDragDirectlyOnHandle, eventPositionAsProportion) {
            if (!isDragDirectlyOnHandle) {
                return undefined;
            }
            var currentHandlePosition;
            if (handleType === SliderBaseWidget_1.HandleType.Left) {
                currentHandlePosition = _this.props.handles.leftHandlePosition;
            }
            else if (handleType === SliderBaseWidget_1.HandleType.Right) {
                currentHandlePosition = _this.props.handles.rightHandlePosition;
            }
            return currentHandlePosition ? currentHandlePosition - eventPositionAsProportion : undefined;
        };
        _this.determineWhichHandleShouldBeDraggedForEvent = function (touchEvent, eventClientCoordinates) {
            if (!touchEvent) {
                if (_this.getHandleCount() === 1) {
                    return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForMouseAndOneHandle(eventClientCoordinates, _this.leftHandleWidgetRef.getBoundingClientRectForIcon());
                }
                else {
                    return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForMouseAndTwoHandles(eventClientCoordinates, _this.leftHandleWidgetRef.getBoundingClientRectForIcon(), _this.rightHandleWidgetRef.getBoundingClientRectForIcon());
                }
            }
            else {
                if (_this.getHandleCount() === 1) {
                    return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForTouchAndOneHandle(eventClientCoordinates, _this.leftHandleWidgetRef.getBoundingClientRectForInteractiveArea());
                }
                else {
                    return SliderHandleContainerDragHandleLogic.determineWhichHandleShouldBeDraggedForTouchAndTwoHandles(eventClientCoordinates, _this.leftHandleWidgetRef.getBoundingClientRectForInteractiveArea(), _this.rightHandleWidgetRef.getBoundingClientRectForInteractiveArea(), _this.rangeHandleElement.getBoundingClientRect());
                }
            }
        };
        _this.fireHandleDragStartEvents = function (handleType, eventPositionAsProportion, singleHandleDragOffsetAsProportion) {
            if (_this.isSingleHandleBeingDragged(handleType)) {
                if (_this.props.onHandleDragStart && !isNaN(eventPositionAsProportion)) {
                    var position = eventPositionAsProportion + (singleHandleDragOffsetAsProportion || 0);
                    _this.props.onHandleDragStart(handleType, position);
                }
            }
            else if (_this.props.onBothHandleDragStart) {
                _this.props.onBothHandleDragStart();
            }
        };
        _this.fireHandleDragMoveEvents = function (leftHandlePosition, rightHandlePosition) {
            if (_this.isSingleHandleBeingDragged(_this.state.handleBeingDragged)) {
                if (_this.props.onHandleDragMove && !isNaN(leftHandlePosition)) {
                    _this.props.onHandleDragMove(_this.state.handleBeingDragged, leftHandlePosition);
                }
            }
            else if (_this.props.onBothHandleDragMove && typeof rightHandlePosition !== 'undefined' &&
                !isNaN(leftHandlePosition) && !isNaN(rightHandlePosition)) {
                _this.props.onBothHandleDragMove(leftHandlePosition, rightHandlePosition);
            }
        };
        _this.handleHandleRelease = function () {
            _this.setState({ handleBeingDragged: undefined });
            if (_this.props.onHandleRelease) {
                _this.props.onHandleRelease();
            }
        };
        _this.setKeyboardFocusOnHandle = function (handleType) {
            var handleElementRefToFocus = null;
            if (handleType === SliderBaseWidget_1.HandleType.Left) {
                handleElementRefToFocus = _this.leftHandleWidgetRef;
            }
            else if (handleType === SliderBaseWidget_1.HandleType.Right) {
                handleElementRefToFocus = _this.rightHandleWidgetRef;
            }
            else if (handleType === SliderBaseWidget_1.HandleType.Both) {
                handleElementRefToFocus = _this.rangeHandleElement;
            }
            handleElementRefToFocus === null || handleElementRefToFocus === void 0 ? void 0 : handleElementRefToFocus.focus();
        };
        _this.dragStart = function (handleType, eventClientCoordinates, isDirectlyOnHandle, startedByTouchEvent) {
            if (!_this.containerElement) {
                return;
            }
            var positionAsProportion = SliderHandleContainerCoordinateLogic.convertClientCoordinateIntoProportionOfContainer(eventClientCoordinates.x, _this.containerElement.getBoundingClientRect());
            _this.globalPointerEventCapturer.addListeners();
            // We set this offset if the user interacted directly w/ the handle, and use it later to avoid jerking the handle
            // directly to the location of the event.
            var singleHandleDragOffsetAsProportion = _this.determineSingleHandleDragOffsetIfNeeded(handleType, isDirectlyOnHandle, positionAsProportion);
            _this.handlePositionsAtDragStart = {
                leftHandlePosition: (handleType === SliderBaseWidget_1.HandleType.Left) ? positionAsProportion : _this.props.handles.leftHandlePosition,
                rightHandlePosition: (handleType === SliderBaseWidget_1.HandleType.Right) ? positionAsProportion : _this.props.handles.rightHandlePosition
            };
            _this.setState({
                handleBeingTouched: startedByTouchEvent ? handleType : undefined,
                handleBeingDragged: handleType,
                dragOriginInClientCoordinates: eventClientCoordinates,
            });
            _this.fireHandleDragStartEvents(handleType, positionAsProportion, singleHandleDragOffsetAsProportion);
            // The reason we are doing this is that, in the case of mouse events, the initial down event may not have been on
            // the handle.  It may have been on the track.  So we have to manually give input focus to the handle.
            _this.setKeyboardFocusOnHandle(handleType);
        };
        _this.dragMove = function (eventClientCoordinates) {
            if (!_this.containerElement) {
                return;
            }
            var isSingleHandleBeingDragged = _this.isSingleHandleBeingDragged(_this.state.handleBeingDragged);
            if (isSingleHandleBeingDragged && _this.props.onHandleDragMove) {
                var handlePositionAtDragStart = _this.state.handleBeingDragged === SliderBaseWidget_1.HandleType.Left ?
                    _this.handlePositionsAtDragStart.leftHandlePosition :
                    _this.handlePositionsAtDragStart.rightHandlePosition;
                var newPosition = SliderHandleContainerCoordinateLogic.calculateProportionalPositionWhenDraggingSingleHandle(_this.state.dragOriginInClientCoordinates.x, handlePositionAtDragStart, eventClientCoordinates.x, _this.containerElement.getBoundingClientRect());
                _this.fireHandleDragMoveEvents(newPosition);
            }
            else if (!isSingleHandleBeingDragged && _this.props.onBothHandleDragMove) {
                // Both handles are being dragged
                var newPositions = SliderHandleContainerCoordinateLogic.calculateProportionalPositionsWhenDraggingBothHandles(_this.state.dragOriginInClientCoordinates.x, _this.handlePositionsAtDragStart.leftHandlePosition, _this.handlePositionsAtDragStart.rightHandlePosition, eventClientCoordinates.x, _this.containerElement.getBoundingClientRect());
                _this.fireHandleDragMoveEvents(newPositions.leftHandlePosition, newPositions.rightHandlePosition);
            }
        };
        _this.handleContainerRef = function (element) {
            _this.containerElement = element;
            // We instantiate this here rather than in the constructor since we need the ref
            if (_this.containerElement) {
                _this.globalPointerEventCapturer = new GlobalPointerEventCapturer_1.GlobalPointerEventCapturer(_this.containerElement, _this.handleGlobalPointerMove, _this.handleGlobalPointerUp);
            }
        };
        _this.setLeftHandleWidgetRef = function (component) {
            _this.leftHandleWidgetRef = component;
        };
        _this.setRightHandleWidgetRef = function (component) {
            _this.rightHandleWidgetRef = component;
        };
        _this.setRangeHandleRef = function (element) {
            _this.rangeHandleElement = element;
        };
        _this.handlePointerDown = function (event) {
            if (_this.props.disabled || !_this.leftHandleWidgetRef || (_this.getHandleCount() === 2 && !_this.rightHandleWidgetRef)) {
                return;
            }
            var castedEvent = event;
            var clientCoordinates = UserInteraction_1.getClientPosition(castedEvent);
            var dragInfo = _this.determineWhichHandleShouldBeDraggedForEvent(UserInteraction_1.isTouchEvent(castedEvent), clientCoordinates);
            if (!dragInfo) {
                return;
            }
            event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
            event.stopPropagation();
            _this.dragStart(dragInfo.handleType, clientCoordinates, dragInfo.directlyOnIcon, UserInteraction_1.isTouchEvent(castedEvent));
        };
        _this.handleGlobalPointerMove = function (event) {
            if (_this.props.disabled || !_this.containerElement) {
                return;
            }
            event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
            event.stopPropagation();
            _this.dragMove(UserInteraction_1.getClientPosition(event));
        };
        _this.handleGlobalPointerUp = function (event) {
            _this.globalPointerEventCapturer.removeListeners();
            _this.handlePositionsAtDragStart = undefined;
            _this.setState({
                handleBeingTouched: undefined,
                dragOriginInClientCoordinates: undefined,
            });
            _this.handleHandleRelease();
            if (_this.props.disabled) {
                return;
            }
            event.preventDefault(); // this will prevent touch events from also firing the corresponding mouse event
            event.stopPropagation();
        };
        _this.makeKeyDownHandler = function (handleType) {
            return function (event) {
                if (_this.props.disabled || !_this.props.onHandleKeyDown) {
                    return;
                }
                if (SliderValueLogic_1.SliderValueLogic.KeysRelevantForManipulatingSlider[event.key]) {
                    _this.setState({ handleBeingDragged: handleType });
                    _this.props.onHandleKeyDown(handleType, event.key);
                    event.preventDefault();
                    event.stopPropagation();
                }
            };
        };
        _this.handleHandleKeyUp = function (event) {
            if (_this.props.disabled) {
                return;
            }
            if (SliderValueLogic_1.SliderValueLogic.KeysRelevantForManipulatingSlider[event.key]) {
                _this.handleHandleRelease();
                event.preventDefault();
                event.stopPropagation();
            }
        };
        _this.handleBlur = function (e) {
            if (!e.currentTarget.contains(e.relatedTarget) && !!_this.state.handleBeingDragged) {
                _this.handleHandleRelease();
            }
        };
        _this.renderLeftHandle = function () {
            if (isNaN(_this.props.handles.leftHandlePosition)) {
                return null;
            }
            var isActive = _this.state.handleBeingDragged === SliderBaseWidget_1.HandleType.Left || _this.state.handleBeingDragged === SliderBaseWidget_1.HandleType.Both;
            var isBeingTouched = _this.state.handleBeingTouched === SliderBaseWidget_1.HandleType.Left || _this.state.handleBeingTouched === SliderBaseWidget_1.HandleType.Both;
            var iconType = _this.getHandleCount() === 1 ? SliderHandleWidget_1.SliderHandleIconType.Normal : SliderHandleWidget_1.SliderHandleIconType.Left;
            return (React.createElement(SliderHandleWidget_1.SliderHandleWidget, { ariaAttributes: _this.props.handlesAriaAttributes.leftHandleAriaAttributes, disabled: _this.props.disabled, iconType: iconType, isActive: isActive, isBeingTouched: isBeingTouched, onKeyDown: _this.makeKeyDownHandler(SliderBaseWidget_1.HandleType.Left), onKeyUp: _this.handleHandleKeyUp, position: _this.props.handles.leftHandlePosition, ref: _this.setLeftHandleWidgetRef, testId: _this.props.testId }));
        };
        _this.renderRightHandle = function () {
            // tslint:disable-next-line:max-line-length
            if (_this.getHandleCount() === 1 || typeof _this.props.handles.rightHandlePosition === 'undefined' || isNaN(_this.props.handles.rightHandlePosition)) {
                return null;
            }
            var isActive = _this.state.handleBeingDragged === SliderBaseWidget_1.HandleType.Right || _this.state.handleBeingDragged === SliderBaseWidget_1.HandleType.Both;
            var isBeingTouched = _this.state.handleBeingTouched === SliderBaseWidget_1.HandleType.Right || _this.state.handleBeingTouched === SliderBaseWidget_1.HandleType.Both;
            return (React.createElement(SliderHandleWidget_1.SliderHandleWidget, { ariaAttributes: _this.props.handlesAriaAttributes.rightHandleAriaAttributes, disabled: _this.props.disabled, iconType: SliderHandleWidget_1.SliderHandleIconType.Right, isActive: isActive, isBeingTouched: isBeingTouched, onKeyDown: _this.makeKeyDownHandler(SliderBaseWidget_1.HandleType.Right), onKeyUp: _this.handleHandleKeyUp, position: _this.props.handles.rightHandlePosition, ref: _this.setRightHandleWidgetRef, testId: _this.props.testId }));
        };
        _this.handleRangeHandleFocusBlur = function (e) {
            if (_this.props.onRangeHandleFocus) {
                _this.props.onRangeHandleFocus(e.type === 'focus');
            }
        };
        _this.state = {};
        return _this;
    }
    SliderHandleContainer.prototype.componentWillUnmount = function () {
        this.globalPointerEventCapturer.removeListeners();
    };
    SliderHandleContainer.prototype.renderRangeHandle = function (style) {
        if (this.props.disabled || isNaN(this.props.handles.leftHandlePosition) || this.getHandleCount() === 1 ||
            // tslint:disable-next-line:triple-equals
            this.props.handles.rightHandlePosition == /* <-- intentional */ undefined || isNaN(this.props.handles.rightHandlePosition)) {
            return null;
        }
        var leftPositionAsPercentage = this.props.handles.leftHandlePosition * 100;
        var rightPositionAsPercentage = 100 - (this.props.handles.rightHandlePosition * 100);
        return (React.createElement("span", { "data-tb-test-id": Declarative_1.makeTestId(this.props.testId, 'RangeHandle'), onBlur: this.handleRangeHandleFocusBlur, onFocus: this.handleRangeHandleFocusBlur, onKeyDown: this.makeKeyDownHandler(SliderBaseWidget_1.HandleType.Both), onKeyUp: this.handleHandleKeyUp, ref: this.setRangeHandleRef, style: tslib_1.__assign(tslib_1.__assign({}, style), { left: leftPositionAsPercentage + "%", right: rightPositionAsPercentage + "%" }), tabIndex: 0 }));
    };
    SliderHandleContainer.prototype.render = function () {
        var dataTestId = Declarative_1.makeTestId(this.props.testId, 'SliderHandleContainer');
        var styles = SliderStyles_1.getSliderHandleContainerStyles();
        var containerStyle = styles.Container;
        if (BrowserDetection_1.isEdge) {
            containerStyle.touchAction = 'none';
        }
        // The container.  This will be the size of the whole slider (sans some padding).
        // The additional onTouchStart handler here is needed for mobile browsers.
        return (React.createElement("div", { "data-tb-test-id": dataTestId, ref: this.handleContainerRef, onBlur: this.handleBlur, onMouseDown: this.handlePointerDown, onTouchStart: this.handlePointerDown, style: containerStyle },
            this.renderLeftHandle(),
            this.renderRangeHandle(styles.RangeHandleArea),
            this.renderRightHandle()));
    };
    return SliderHandleContainer;
}(React.Component));
exports.SliderHandleContainer = SliderHandleContainer;
//# sourceMappingURL=SliderHandleContainer.js.map